import React, { ReactElement } from 'react';
import { Tag } from 'antd';
import { FileImageOutlined, FilePdfOutlined } from '@ant-design/icons';

import { DocumentStatus, DocumentTypeId, PuppyStatus } from 'pupdocs/utilities/const';
import {
  DocumentStatuses,
  DocumentTypeIds,
  LineItemDocument,
  LineItemMedia,
  LineItemMeta,
  PuppyStatuses,
} from 'pupdocs/types';
import { Breeder, Customer, Puppy } from 'types';
import {
  FileDateStyled,
  FileTypeStatusContainer,
  TypeStatusStyled,
} from '../detailsContent/DetailsContent.styles';

export const getFileTypeIcon = (fileName: string): ReactElement => {
  const fileExtension = fileName.substr(fileName.lastIndexOf('.') + 1);

  if (fileExtension === 'pdf')
    return <FilePdfOutlined style={{ fontSize: '50px', color: 'rgba(0, 0, 0, 0.65)' }} />;

  return (
    <>
      <FileImageOutlined style={{ fontSize: '50px', color: 'rgba(0, 0, 0, 0.65)' }} />
    </>
  );
};

export const renderMediaDocuments = (media: LineItemMedia): ReactElement => {
  return (
    <>
      {media.documents.map((document: LineItemDocument) => {
        return (
          <FileTypeStatusContainer key={document.documentTypeId}>
            <TypeStatusStyled>
              {getDocumentTitle(document.documentTypeId)}
              {getDocumentStatus(document?.workflowStatus?.name)}
            </TypeStatusStyled>
            <FileDateStyled>Expires on 04/25/21</FileDateStyled>
          </FileTypeStatusContainer>
        );
      })}
    </>
  );
};

export const getPuppyStatus = (meta: LineItemMeta): PuppyStatuses => {
  if (meta.isFinalized) return PuppyStatus.Finalized;
  if (meta.isInReview) return PuppyStatus.InReview;
  if (meta.isPendingReview) return PuppyStatus.PendingReview;
  return PuppyStatus.Urgent;
};

export const getDocumentTitle = (documentTypeId: DocumentTypeIds): ReactElement => {
  switch (documentTypeId) {
    case DocumentTypeId.HealthCertificate:
      return <span>Health Certificate</span>;
    case DocumentTypeId.VeterinaryHealthReport:
      return <span>Veterinary Health Report </span>;
    default:
      return <span>Vaccination Record </span>;
  }
};

export const getDocumentStatus = (
  documentStatus: DocumentStatuses | undefined,
): ReactElement => {
  switch (documentStatus) {
    case DocumentStatus.Approved:
      return <Tag color="green">Approved</Tag>;
    case DocumentStatus.HealthReview:
      return <Tag color="purple">{documentStatus}</Tag>;
    case DocumentStatus.PendingReview:
      return <Tag color="cyan">{documentStatus}</Tag>;
    default:
      return <Tag>Not included</Tag>;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapPuppy = (item: any): Puppy => {
  return {
    id: item?.puppy.id,
    name: item?.puppy?.buyerName || item?.puppy?.salesName || item?.puppy?.breederName,
    dateOfBirth: item?.litter?.birthDate,
    dateAvailable: item?.puppy.dateAvailable,
    breed: item?.litter?.breed.name,
    lastWeighedAt: item?.puppy.lastWeighedAt,
    weightRange: item?.puppy?.weightRange?.name,
    microchipRfid: item?.puppy?.microchipRfid,
    soldDate: item?.order?.purchasedAt,
    gender: '--',
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapCustomer = (item: any): Customer => {
  return {
    id: item?.customer?.id,
    address: item?.customer?.address?.address,
    stateAbbr: item?.customer?.address?.state?.abbr,
    city: item?.customer?.address?.city,
    countryAbbr: 'US',
    phone: item?.customer?.phone?.phoneNumber,
    email: item?.customer?.email?.emailAddress,
    postalCode: item?.customer?.address?.postalCode,
    firstName: item?.customer?.firstName,
    lastName: item?.customer?.lastName,
  };
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapBreeder = (item: any): Breeder => {
  return {
    id: item?.breeder?.id,
    address: item?.breeder?.address?.address,
    stateAbbr: item?.breeder?.address?.state?.abbr,
    city: item?.breeder?.address?.city,
    countryAbbr: 'US',
    phone: item?.breeder?.phone?.phoneNumber,
    email: item?.breeder?.email?.emailAddress,
    postalCode: item?.breeder?.address?.postalCode,
    name: item?.breeder?.companyName,
    breederTier: '--',
  };
};
